import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader } from 'lucide-react';
import { subtaskService } from '../../services/subtaskService';
import AssignmentPicker from './AssignmentPicker';
import toast from 'react-hot-toast';
import { useStatus } from '../../contexts/StatusContext';

interface SubtaskCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentTaskId: string;
  onSubtaskCreated: (subtask: any) => void;
}

const SubtaskCreateModal: React.FC<SubtaskCreateModalProps> = ({
  isOpen,
  onClose,
  parentTaskId,
  onSubtaskCreated
}) => {
  const { user_id, family_id } = useStatus();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: ''
  });

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        description: '',
        assigned_to: ''
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter a subtask title');
      return;
    }

    setLoading(true);

    try {
      // Create subtask data
      const subtaskData = {
        title: formData.title,
        description: formData.description,
        parent_task_id: parentTaskId,
        assigned_to: formData.assigned_to || undefined,
        family_id: family_id || undefined,
        created_by: user_id || undefined,
        task_type: 'subtask',
        status: 'pending',
        priority_score: 50, // Default medium priority
        category: 'general',
        difficulty: 'medium'
      };

      console.log('[SubtaskCreateModal] Creating subtask:', subtaskData);
      const createdSubtask = await subtaskService.createSubtask(parentTaskId, subtaskData);

      onSubtaskCreated(createdSubtask);
      onClose();
    } catch (error) {
      console.error('[SubtaskCreateModal] Failed to create subtask:', error);
      toast.error('Failed to create subtask. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = (userId: string | null) => {
    setFormData(prev => ({ ...prev, assigned_to: userId || '' }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Create Subtask
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label
                  htmlFor="subtask-title"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Subtask Title *
                </label>
                <input
                  id="subtask-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Enter subtask title..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-honey-500 focus:border-transparent"
                  autoFocus
                  disabled={loading}
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="subtask-description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Description (Optional)
                </label>
                <textarea
                  id="subtask-description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Enter subtask description..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-honey-500 focus:border-transparent resize-none"
                  disabled={loading}
                />
              </div>

              {/* Assignment Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assign To (Optional)
                </label>
                <AssignmentPicker
                  selectedUserId={formData.assigned_to || null}
                  onSelect={handleAssignmentChange}
                  disabled={loading}
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-honey-500 hover:bg-honey-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !formData.title.trim()}
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Subtask'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubtaskCreateModal;
